import { useState, useEffect } from 'react';
import axios from 'axios';
import { PenLine, Star, Beaker, FileText, Eye, Download, Webhook, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SettingsManager({ settings, onRefresh, headers }) {
    const [formData, setFormData] = useState({
        writings_empty_message: settings.writings_empty_message || '',
        recommendations_empty_message: settings.recommendations_empty_message || '',
        experiments_empty_message: settings.experiments_empty_message || '',
        research_empty_message: settings.research_empty_message || '',
        flip_card_hint: settings.flip_card_hint || '',
        n8n_webhook_url: settings.n8n_webhook_url || ''
    });
    const [saving, setSaving] = useState(false);
    const [testingWebhook, setTestingWebhook] = useState(false);
    const [cvUploading, setCvUploading] = useState(false);
    const [cvInfo, setCvInfo] = useState({
        filename: settings.cv_filename || '',
        original_name: settings.cv_original_name || '',
        uploaded_at: settings.cv_uploaded_at || ''
    });

    useEffect(() => {
        setFormData({
            writings_empty_message: settings.writings_empty_message || '',
            recommendations_empty_message: settings.recommendations_empty_message || '',
            experiments_empty_message: settings.experiments_empty_message || '',
            research_empty_message: settings.research_empty_message || '',
            flip_card_hint: settings.flip_card_hint || '',
            n8n_webhook_url: settings.n8n_webhook_url || ''
        });
        setCvInfo({
            filename: settings.cv_filename || '',
            original_name: settings.cv_original_name || '',
            uploaded_at: settings.cv_uploaded_at || ''
        });
    }, [settings]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/settings`, formData, { headers });
            toast.success('Settings saved!');
            onRefresh();
        } catch (error) {
            toast.error('Failed to save settings');
        }
        setSaving(false);
    };

    const handleTestWebhook = async () => {
        if (!formData.n8n_webhook_url) {
            toast.error('Please enter a webhook URL first');
            return;
        }
        setTestingWebhook(true);
        try {
            await axios.put(`${API}/settings`, { n8n_webhook_url: formData.n8n_webhook_url }, { headers });
            const res = await axios.post(`${API}/webhook/test`, {}, { headers });
            if (res.data.success) {
                toast.success('Webhook connected successfully!');
            } else {
                toast.error(`Webhook test failed: ${res.data.error || 'Unknown error'}`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to test webhook');
        }
        setTestingWebhook(false);
    };

    const handleCvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const allowedTypes = ['.pdf', '.doc', '.docx'];
        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!allowedTypes.includes(ext)) {
            toast.error('Only PDF, DOC, DOCX files are allowed');
            return;
        }

        setCvUploading(true);
        const uploadData = new FormData();
        uploadData.append('file', file);

        try {
            const res = await axios.post(`${API}/cv/upload`, uploadData, {
                headers: { 
                    ...headers,
                    'Content-Type': 'multipart/form-data'
                }
            });
            toast.success('CV uploaded successfully!');
            setCvInfo({
                filename: res.data.filename,
                original_name: res.data.original_name,
                uploaded_at: new Date().toISOString()
            });
            onRefresh();
        } catch (error) {
            toast.error('Failed to upload CV');
        }
        setCvUploading(false);
        e.target.value = '';
    };

    const handleCvDelete = async () => {
        try {
            await axios.delete(`${API}/cv`, { headers });
            toast.success('CV deleted');
            setCvInfo({ filename: '', original_name: '', uploaded_at: '' });
            onRefresh();
        } catch (error) {
            toast.error('Failed to delete CV');
        }
    };

    return (
        <div className="retro-card p-6" data-testid="settings-manager">
            <div className="mb-6">
                <h2 className="text-lg uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                    Site Settings
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                    Customize the empty state messages shown on your landing page
                </p>
            </div>

            <div className="space-y-6">
                {/* Writings Message */}
                <div>
                    <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
                        <PenLine className="h-4 w-4 text-primary" />
                        Writings Section Message
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                        This message always appears at the top of the Writings section
                    </p>
                    <Textarea 
                        className="rounded-none border-2 mt-1" 
                        value={formData.writings_empty_message} 
                        onChange={(e) => setFormData({...formData, writings_empty_message: e.target.value})}
                        placeholder="Enter a quirky message for the writings section..."
                        data-testid="settings-writings-message"
                    />
                </div>

                {/* Recommendations Message */}
                <div>
                    <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
                        <Star className="h-4 w-4 text-primary" />
                        Recommendations Empty Message
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                        Shown when there are no published recommendations (disappears when content is added)
                    </p>
                    <Textarea 
                        className="rounded-none border-2 mt-1" 
                        value={formData.recommendations_empty_message} 
                        onChange={(e) => setFormData({...formData, recommendations_empty_message: e.target.value})}
                        placeholder="Enter a message for when recommendations are empty..."
                        data-testid="settings-recommendations-message"
                    />
                </div>

                {/* Experiments Message */}
                <div>
                    <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
                        <Beaker className="h-4 w-4 text-primary" />
                        Experiments Empty Message
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                        Shown when there are no published experiments (disappears when content is added)
                    </p>
                    <Textarea 
                        className="rounded-none border-2 mt-1" 
                        value={formData.experiments_empty_message} 
                        onChange={(e) => setFormData({...formData, experiments_empty_message: e.target.value})}
                        placeholder="Enter a message for when experiments are empty..."
                        data-testid="settings-experiments-message"
                    />
                </div>

                {/* Research Message */}
                <div>
                    <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        Research Empty Message
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                        Shown when there is no published research (disappears when content is added)
                    </p>
                    <Textarea 
                        className="rounded-none border-2 mt-1" 
                        value={formData.research_empty_message} 
                        onChange={(e) => setFormData({...formData, research_empty_message: e.target.value})}
                        placeholder="Enter a message for when research is empty..."
                        data-testid="settings-research-message"
                    />
                </div>

                {/* Flip Card Hint */}
                <div>
                    <Label className="text-xs uppercase tracking-widest flex items-center gap-2">
                        <Eye className="h-4 w-4 text-primary" />
                        Flip Card Hint Text
                    </Label>
                    <p className="text-xs text-muted-foreground mb-2">
                        Text shown below the Prince portrait on the hero flip card
                    </p>
                    <Input 
                        className="rounded-none border-2 mt-1" 
                        value={formData.flip_card_hint} 
                        onChange={(e) => setFormData({...formData, flip_card_hint: e.target.value})}
                        placeholder="flip to reveal your new fav character"
                        data-testid="settings-flip-card-hint"
                    />
                </div>

                <Button 
                    onClick={handleSave}
                    disabled={saving}
                    className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn text-xs uppercase tracking-widest"
                    data-testid="save-settings-btn"
                >
                    {saving ? 'Saving...' : 'Save Settings'}
                </Button>

                {/* CV Upload Section */}
                <div className="pt-6 mt-6 border-t-2 border-dashed border-border">
                    <Label className="text-xs uppercase tracking-widest flex items-center gap-2 mb-2">
                        <Download className="h-4 w-4 text-primary" />
                        CV / Resume Upload
                    </Label>
                    <p className="text-xs text-muted-foreground mb-4">
                        Upload your CV (PDF, DOC, DOCX). Visitors can download it from the footer.
                    </p>
                    
                    {cvInfo.filename ? (
                        <div className="flex items-center gap-4 p-4 bg-muted/50 border-2 border-border">
                            <div className="flex-1">
                                <p className="text-sm font-medium" style={{ fontFamily: "'Silkscreen', cursive" }}>
                                    {cvInfo.original_name}
                                </p>
                                {cvInfo.uploaded_at && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Uploaded: {new Date(cvInfo.uploaded_at).toLocaleDateString()}
                                    </p>
                                )}
                            </div>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`${API}/cv/download`, '_blank')}
                                className="rounded-none border-2 text-xs uppercase"
                                data-testid="preview-cv-btn"
                            >
                                Preview
                            </Button>
                            <Button
                                variant="destructive"
                                size="sm"
                                onClick={handleCvDelete}
                                className="rounded-none border-2 text-xs uppercase"
                                data-testid="delete-cv-btn"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    ) : (
                        <p className="text-sm text-muted-foreground italic mb-4">No CV uploaded yet</p>
                    )}
                    
                    <div className="mt-4">
                        <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleCvUpload}
                            className="hidden"
                            id="cv-upload"
                            data-testid="cv-upload-input"
                        />
                        <label htmlFor="cv-upload">
                            <Button
                                asChild
                                disabled={cvUploading}
                                className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn text-xs uppercase tracking-widest cursor-pointer"
                            >
                                <span>
                                    {cvUploading ? 'Uploading...' : cvInfo.filename ? 'Replace CV' : 'Upload CV'}
                                </span>
                            </Button>
                        </label>
                    </div>
                </div>

                {/* n8n Webhook Integration */}
                <div className="pt-6 mt-6 border-t-2 border-dashed border-border">
                    <Label className="text-xs uppercase tracking-widest flex items-center gap-2 mb-2">
                        <Webhook className="h-4 w-4 text-primary" />
                        n8n Webhook Integration
                    </Label>
                    <p className="text-xs text-muted-foreground mb-4">
                        Connect to your n8n workflow to auto-publish writings to multiple platforms (Twitter, LinkedIn, etc.)
                    </p>
                    
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs uppercase tracking-widest">Webhook URL</Label>
                            <Input 
                                className="rounded-none border-2 mt-1" 
                                value={formData.n8n_webhook_url} 
                                onChange={(e) => setFormData({...formData, n8n_webhook_url: e.target.value})}
                                placeholder="https://your-n8n-instance.com/webhook/..."
                                data-testid="settings-n8n-webhook"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button 
                                onClick={handleTestWebhook}
                                disabled={testingWebhook || !formData.n8n_webhook_url}
                                variant="outline"
                                className="rounded-none border-2 text-xs uppercase tracking-widest"
                                data-testid="test-webhook-btn"
                            >
                                <RefreshCw className={`h-4 w-4 mr-2 ${testingWebhook ? 'animate-spin' : ''}`} />
                                {testingWebhook ? 'Testing...' : 'Test Connection'}
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Once configured, you can click "n8n" button on any published writing to send it to your workflow.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
